import { useMelwaysInfo } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import {
  Stack,
  RadioGroup,
  RadioItem,
  Badge,
  Strong,
} from 'braid-design-system';
import type { FormEvent } from 'react';
import { LoadingItem } from 'src/components/LoadingItem/LoadingItem';
import { PageTitles } from 'src/components/Notifications/pageTitle';
import translations from 'src/components/Visibility/ProfileVisibility/.vocab';
import {
  type AnalyticsProfileVisibilityLevel,
  trackProfileVisibilityUpdated,
} from 'src/modules/analytics/profileAnalytics';
import {
  useGetProfileVisibility,
  useUpdateProfileVisibility,
} from 'src/modules/graphql/visibility/data';
import { useTypedSelector } from 'src/reducers';
import { VisibilityLevel } from 'src/types/graphql';

export const EditProfileVisibilitySettings = () => {
  const { t } = useTranslations(translations);
  const brand = useTypedSelector((store) => store.page.pageAnalytics.brand);
  const brandLabel = PageTitles[brand];
  const { profileVisibilityLevel, loading, error } = useGetProfileVisibility();
  const { updateProfileVisibility } = useUpdateProfileVisibility();
  const { language } = useMelwaysInfo();

  const PROFILE_VISIBILITY_OPTION_DESCRIPTION = {
    Standard: t('Employers can view my Profile and...', {
      brand: brandLabel,
    }),
    Limited: t('Employers can view my Profile, but...', {
      brand: brandLabel,
    }),
    Hidden: t('Employers cannot search for me...'),
  };

  const onChangeFn = ({
    currentTarget: { value },
  }: FormEvent<HTMLInputElement>) => {
    updateProfileVisibility({
      variables: {
        input: {
          level: value as VisibilityLevel,
        },
        languageCode: language,
      },
    });

    trackProfileVisibilityUpdated(
      value.toLowerCase() as AnalyticsProfileVisibilityLevel,
    );
  };

  return loading || error ? (
    <Stack space="large">
      <LoadingItem size="standard" width="small" />
      <LoadingItem size="standard" width="large" />
      <LoadingItem size="standard" width="large" />
      <LoadingItem size="standard" width="large" />
    </Stack>
  ) : (
    <Stack space="large">
      <RadioGroup
        id="option"
        label={t('Set visibility')}
        onChange={onChangeFn}
        value={profileVisibilityLevel || ''}
        data={{ automation: 'profile-visibility-options' }}
      >
        <RadioItem
          data={{ automation: 'standard-radio-option' }}
          badge={<Badge tone="promote">{t('Recommended')}</Badge>}
          label={<Strong>{t('Standard')}</Strong>}
          description={
            PROFILE_VISIBILITY_OPTION_DESCRIPTION[VisibilityLevel.Standard]
          }
          value="Standard"
        />
        <RadioItem
          data={{ automation: 'limited-radio-option' }}
          label={<Strong>{t('Limited')}</Strong>}
          description={
            PROFILE_VISIBILITY_OPTION_DESCRIPTION[VisibilityLevel.Limited]
          }
          value="Limited"
        />
        <RadioItem
          data={{ automation: 'hidden-radio-option' }}
          label={<Strong>{t('Hidden')}</Strong>}
          description={
            PROFILE_VISIBILITY_OPTION_DESCRIPTION[VisibilityLevel.Hidden]
          }
          value="Hidden"
        />
      </RadioGroup>
    </Stack>
  );
};
